import { formatOraclePrice } from 'common/perps/oracle-display'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { PerpContract } from 'common/contract'
import { formatPerpClosePercent, inferPriceDecimals } from 'common/perps/format'
import { formatMoney, formatMoneyPrecise } from 'common/util/format'
import { Col } from 'web/components/layout/col'
import { Row } from 'web/components/layout/row'
import { RelativeTimestamp } from 'web/components/relative-timestamp'
import { LoadMoreUntilNotVisible } from 'web/components/widgets/visibility-observer'
import { LoadingIndicator } from 'web/components/widgets/loading-indicator'
import { InfoTooltip } from 'web/components/widgets/info-tooltip'
import ShortToggle from 'web/components/widgets/short-toggle'
import { UserAvatarAndBadge } from 'web/components/widgets/user-link'
import { useHideApiTrades } from 'web/hooks/use-hide-api-trades'
import { useIsMobile } from 'web/hooks/use-is-mobile'
import { api } from 'web/lib/api/api'
import { track } from 'web/lib/service/analytics'
import { POSITIONS_POLL_MS as POLL_MS } from './use-perp-positions'

type Event = {
  id: number
  ts: number
  userId: string | null
  direction: 'long' | 'short' | null
  eventType: 'open' | 'add' | 'close' | 'liquidation' | 'adl' | 'funding'
  oraclePrice: number
  sizeDelta: number
  costBasisDelta: number
  originalCostBasisDelta: number
  leverage: number | null
  payout: number | null
  pnl: number | null
  adlFactor: number | null
  fraction: number | null
  isApi: boolean
  userName: string | null
  username: string | null
  avatarUrl: string | null
}

const PAGE_SIZE = 50

export const PerpTradesTab = (props: {
  contract: PerpContract
  setTotalTrades?: (n: number) => void
}) => {
  const { contract, setTotalTrades } = props
  const [events, setEvents] = useState<Event[] | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const initializedRef = useRef(false)
  // Bumped whenever the list resets for a new filter, so a page requested
  // under the old filter can be dropped when it arrives.
  const filterEpochRef = useRef(0)
  // Filtering is server-side (like the bets tab): dropping API rows on the
  // client would leave short, ragged pages since pagination counts rows
  // before the filter.
  const [hideApiTrades, setHideApiTrades] = useHideApiTrades()
  // Hoisted out of EventRow: one resize listener for the tab, not one per row.
  const isMobile = useIsMobile(800)

  // Poll the first page so new trades stream in while the tab is open (this
  // used to fetch exactly once — the tab froze until a full page reload).
  // New events have larger ids and come newest-first, so polling merges by
  // prepending unseen rows without disturbing pagination.
  useEffect(() => {
    let cancelled = false
    filterEpochRef.current++
    initializedRef.current = false
    setEvents(null)
    setHasMore(true)
    // A page still loading for the old filter mustn't block the new list.
    setLoadingMore(false)
    const load = () =>
      api('get-perp-events', {
        contractId: contract.id,
        limit: PAGE_SIZE,
        // Omitted rather than sent as false: the props schema is strict, so
        // an unknown key 400s. Web deploys ahead of the API, and the default
        // view must keep working against an API that predates this filter.
        ...(hideApiTrades ? { excludeApi: true } : {}),
      })
        .then((rows) => {
          if (cancelled) return
          if (!initializedRef.current) {
            initializedRef.current = true
            setHasMore(rows.length === PAGE_SIZE)
          }
          setEvents((prev) => {
            if (!prev) return rows
            const known = new Set(prev.map((e) => e.id))
            const unseen = rows.filter((r) => !known.has(r.id))
            return unseen.length ? [...unseen, ...prev] : prev
          })
        })
        .catch(() => {})
    load()
    const id = setInterval(load, POLL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [contract.id, hideApiTrades])

  // Count is a lower bound while pagination is in progress. Good enough for
  // the tab title in v1.
  useEffect(() => {
    if (events) setTotalTrades?.(events.length)
  }, [events?.length])

  const loadMore = async () => {
    if (!events || !hasMore || loadingMore) return false
    const epoch = filterEpochRef.current
    setLoadingMore(true)
    try {
      const oldest = events[events.length - 1]
      const more = await api('get-perp-events', {
        contractId: contract.id,
        beforeId: oldest.id,
        limit: PAGE_SIZE,
        ...(hideApiTrades ? { excludeApi: true } : {}),
      })
      // Stale: the list was reset for another filter while this loaded.
      if (filterEpochRef.current !== epoch) return true
      if (more.length === 0) {
        setHasMore(false)
        return false
      }
      setEvents((prev) => (prev ? [...prev, ...more] : more))
      setHasMore(more.length === PAGE_SIZE)
      return true
    } finally {
      // After a reset, loadingMore belongs to the new list's requests.
      if (filterEpochRef.current === epoch) setLoadingMore(false)
    }
  }

  const filterRow = (
    <Row className="bg-canvas-50 items-center gap-2 rounded-lg px-3 py-2 sm:px-4">
      <span className="text-ink-500 text-xs font-medium uppercase tracking-wide">
        Hide API trades
      </span>
      <ShortToggle
        on={hideApiTrades}
        setOn={(enabled) => {
          setHideApiTrades(enabled)
          track('toggle-hide-api-trades-filter', {
            contractSlug: contract.slug,
            contractName: contract.question,
            hideApiTrades: enabled,
            isPerp: true,
          })
        }}
        size="sm"
      />
    </Row>
  )

  const priceDecimals = inferPriceDecimals([
    Number(contract.oraclePrice),
    ...(events ?? []).map((e) => e.oraclePrice),
  ])

  return (
    <Col className="gap-2">
      {filterRow}
      {!events ? (
        <LoadingIndicator />
      ) : events.length === 0 ? (
        <div className="text-ink-500 p-4 text-sm">
          {hideApiTrades ? 'No manual trades yet.' : 'No trades yet.'}
        </div>
      ) : (
        <>
          {events.map((e) => (
            <EventRow
              feedId={contract.oracleFeedId}
              key={e.id}
              event={e}
              priceDecimals={priceDecimals}
              short={isMobile}
            />
          ))}
          {hasMore && <LoadMoreUntilNotVisible loadMore={loadMore} />}
        </>
      )}
    </Col>
  )
}

const EVENT_LABELS: Record<Event['eventType'], string> = {
  open: 'opened',
  add: 'added to',
  close: 'closed',
  liquidation: 'was liquidated on',
  adl: 'was auto-deleveraged on',
  funding: 'funding',
}

const EventRow = (props: {
  feedId: string
  event: Event
  priceDecimals: number
  short: boolean
}) => {
  const { event, priceDecimals, short } = props
  const dirColor =
    event.direction === 'long'
      ? 'text-teal-600'
      : event.direction === 'short'
      ? 'text-scarlet-600'
      : 'text-ink-600'

  // For open/add we show deposited margin (originalCostBasisDelta).
  // For close/liquidation/adl we show payout + PnL from data.
  const isExit =
    event.eventType === 'close' ||
    event.eventType === 'liquidation' ||
    event.eventType === 'adl'
  const isLiquidation = event.eventType === 'liquidation'

  const marginOrPayout = isLiquidation
    ? null
    : isExit
    ? event.payout
    : event.originalCostBasisDelta > 0
    ? event.originalCostBasisDelta
    : null
  const liquidationMarginLost =
    isLiquidation && Number.isFinite(event.originalCostBasisDelta)
      ? Math.abs(event.originalCostBasisDelta)
      : null
  // A partial close keeps the row open, so the tape must not read as an exit.
  // Its `leverage` is the SURVIVOR's — unchanged by the split — which is the
  // right number to show beside "partly closed".
  const partialClose =
    event.eventType === 'close' && event.fraction != null && event.fraction < 1
      ? event.fraction
      : null
  const partialAdlReduction =
    event.eventType === 'adl' &&
    event.payout == null &&
    event.adlFactor != null &&
    Number.isFinite(event.adlFactor)
      ? Math.max(0, Math.min(1, 1 - event.adlFactor))
      : null

  return (
    <Row className="bg-canvas-0 border-ink-200 flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2.5 sm:flex-nowrap sm:px-4 sm:py-3">
      <div className="min-w-0 flex-1 sm:flex-none">
        <UserAvatarAndBadge
          user={{
            id: event.userId ?? '',
            name: event.userName ?? 'anon',
            username: event.username ?? 'anon',
            avatarUrl: event.avatarUrl ?? '',
          }}
          short={short}
        />
      </div>
      <Col className="order-last min-w-0 basis-full sm:order-none sm:flex-1 sm:basis-auto">
        <Row className="flex-wrap items-center gap-1 text-sm">
          {/* Aggregate rows (per-tick ADL) carry no direction; drop the
              trailing "on" so the label doesn't dangle before the price. */}
          <span className="text-ink-600">
            {partialClose != null
              ? `closed ${formatPerpClosePercent(partialClose)} of`
              : event.direction
              ? EVENT_LABELS[event.eventType]
              : EVENT_LABELS[event.eventType].replace(/ on$/, '')}
          </span>
          {event.direction && (
            <span className={clsx('font-semibold uppercase', dirColor)}>
              {event.direction}
            </span>
          )}
          {event.leverage != null && event.leverage > 0 && (
            <span className="text-ink-500">{event.leverage.toFixed(2)}×</span>
          )}
          <span className="text-ink-500">
            @{' '}
            {formatOraclePrice(props.feedId, event.oraclePrice, priceDecimals)}
          </span>
          <RelativeTimestamp
            time={event.ts}
            shortened
            className="text-ink-400 !ml-1 text-xs"
          />
          {event.isApi && (
            <InfoTooltip text="Placed via the API" className="!ml-1">
              🤖
            </InfoTooltip>
          )}
        </Row>
      </Col>
      <Col className="ml-auto shrink-0 items-end text-sm">
        {isLiquidation ? (
          <span className="text-scarlet-600 font-medium">
            {liquidationMarginLost == null
              ? 'Margin lost'
              : `${formatMoneyPrecise(liquidationMarginLost)} margin lost`}
          </span>
        ) : partialAdlReduction != null ? (
          <span className="font-medium text-amber-600">
            {(partialAdlReduction * 100).toFixed(1)}% position reduced
          </span>
        ) : marginOrPayout != null ? (
          // Payouts and PnL are fractional and match the position history
          // rows; posted margin stays whole-mana like everywhere else.
          <span className="text-ink-900">
            {isExit
              ? formatMoneyPrecise(marginOrPayout)
              : formatMoney(marginOrPayout)}
          </span>
        ) : null}
        {isExit && event.pnl != null && (
          <span
            className={event.pnl >= 0 ? 'text-teal-600' : 'text-scarlet-600'}
          >
            {event.pnl >= 0 ? '+' : ''}
            {formatMoneyPrecise(event.pnl)}
          </span>
        )}
      </Col>
    </Row>
  )
}
